import { SetState, GetState } from 'zustand';
import {
  getInvoiceByExternalId,
  isInvoiceStatusPaid, isInvoiceStatusSent, isInvoiceStatusUnsent,
  mapDataForInvoicePage,
  removeDuplicateObjectsByExternalId,
  removeItemByExternalId,
} from '../pages/Invoices/InvoiceHub/utils';
import { IFlattenServerErrorResponse, IServerErrorResponse } from '../types';
import { flattenServerErrorResponse } from '../helpers/errors.helper';

export interface IInvoicesSlice {
  outstandingInvoices: any;
  setOutstandingInvoices: (invoices: any, shouldConcat: boolean) => void;
  getOutstandingInvoices: () => any;
  outstandingPageNumber: number;
  setOutstandingPageNumber: (pageNumber: number) => void;
  resetOutstandingInvoices: () => void;
  paidInvoices: any;
  setPaidInvoices: (invoices: any, shouldConcat: boolean) => void;
  getPaidInvoices: () => any;
  paidPageNumber: number;
  setPaidPageNumber: (pageNumber: number) => void;
  canceledInvoices: any;
  setCanceledInvoices: (invoices: any, shouldConcat: boolean) => void;
  getCanceledInvoices: () => any;
  canceledPageNumber: number;
  setCanceledPageNumber: (pageNumber: number) => void;
  setSelectedInvoice: (invoiceNumber: string) => void;
  getSelectedInvoice: () => any;
  selectedInvoice: any;
  setActiveModal: (modalName: string) => void;
  getActiveModal: () => any;
  activeModal: string;
  resetAll: () => void;
  removeInvoiceFromList: (invoiceStatus: string, externalId: string) => void;
  resetHistoryTab: () => void;
  resetUnpaidTab: () => void;
  error: IFlattenServerErrorResponse | null;
  setError: (error: IServerErrorResponse) => void;
  resetError: () => void;
  resetInvoicesSlice: () => void;
}
const createInvoicesSlice = (set: SetState<IInvoicesSlice>, get: GetState<IInvoicesSlice>) => ({
  outstandingInvoices: [],
  outstandingPageNumber: 1,
  paidInvoices: [],
  paidPageNumber: 1,
  canceledInvoices: [],
  canceledPageNumber: 1,
  selectedInvoice: {},
  activeModal: '',
  error: null,
  resetInvoicesSlice: () => {
    set({
      outstandingInvoices: [],
      outstandingPageNumber: 1,
      paidInvoices: [],
      paidPageNumber: 1,
      canceledInvoices: [],
      canceledPageNumber: 1,
      selectedInvoice: {},
      activeModal: '',
      error: null,
    });
  },
  setOutstandingInvoices: (invoices: any, shouldConcat: boolean) => {
    if (!shouldConcat) set({ outstandingInvoices: removeDuplicateObjectsByExternalId(invoices) });
    else set((prevState) => ({ outstandingInvoices: removeDuplicateObjectsByExternalId([...prevState.outstandingInvoices, ...invoices]) }));
  },
  setOutstandingPageNumber: (pageNumber: number) => {
    set({ outstandingPageNumber: pageNumber });
  },
  setPaidInvoices: (invoices: any, shouldConcat: boolean) => {
    if (!shouldConcat) set({ paidInvoices: removeDuplicateObjectsByExternalId(invoices) });
    else set((prevState) => ({ paidInvoices: removeDuplicateObjectsByExternalId([...prevState.paidInvoices, ...invoices]) }));
  },
  setPaidPageNumber: (pageNumber: number) => {
    set({ paidPageNumber: pageNumber });
  },
  setCanceledInvoices: (invoices: any, shouldConcat: boolean) => {
    if (!shouldConcat) set({ canceledInvoices: removeDuplicateObjectsByExternalId(invoices) });
    else set((prevState) => ({ canceledInvoices: removeDuplicateObjectsByExternalId([...prevState.canceledInvoices, ...invoices]) }));
  },
  setCanceledPageNumber: (pageNumber: number) => {
    set({ canceledPageNumber: pageNumber });
  },
  setSelectedInvoice: (externalId: string) => {
    const invoice = getInvoiceByExternalId([...get().outstandingInvoices, ...get().paidInvoices, ...get().canceledInvoices], externalId);
    set({ selectedInvoice: mapDataForInvoicePage(invoice) });
  },
  setActiveModal: (modalName: string) => set({ activeModal: modalName }),
  getOutstandingInvoices: () => get().outstandingInvoices,
  resetOutstandingInvoices: () => {
    set(() => (
      { outstandingInvoices: [] }
    ));
  },
  getPaidInvoices: () => get().paidInvoices,
  getCanceledInvoices: () => get().canceledInvoices,
  getSelectedInvoice: () => get().selectedInvoice,
  getActiveModal: () => get().activeModal,
  resetAll: () => {
    set({
      outstandingInvoices: [],
      outstandingPageNumber: 1,
      paidInvoices: [],
      paidPageNumber: 1,
      canceledInvoices: [],
      canceledPageNumber: 1,
      selectedInvoice: {},
      activeModal: '',
      error: null,
    });
  },
  resetUnpaidTab: () => {
    set({
      outstandingInvoices: [],
      outstandingPageNumber: 1,
    });
  },
  resetHistoryTab: () => {
    set({
      paidInvoices: [],
      paidPageNumber: 1,
      canceledInvoices: [],
      canceledPageNumber: 1,
    });
  },
  removeInvoiceFromList: (invoiceStatus: string, externalId: string) => {
    if (isInvoiceStatusPaid(invoiceStatus)) set({ paidInvoices: removeItemByExternalId(get().paidInvoices, externalId) });
    if (isInvoiceStatusUnsent(invoiceStatus) || isInvoiceStatusSent((invoiceStatus))) set({ outstandingInvoices: removeItemByExternalId(get().outstandingInvoices, externalId) });
  },
  setError: (error: IServerErrorResponse) => {
    set({ error: flattenServerErrorResponse(error) });
  },
  resetError: () => {
    set({ error: null });
  },
});

export default createInvoicesSlice;
